import { z } from 'zod';

/** Product policy approved 2026-09-08; not a universal statutory classification. */
export const ARCHIVE_RETENTION_POLICY = 'trivoa.archive.ten-calendar-years.v1';

/** German calendar year; exclusive UTC expiry conservatively includes the final CET hour. */
export function archiveRetentionUntil(archivedAt: Date): string {
  if (!Number.isFinite(archivedAt.getTime())) throw new Error('ARCHIVE_RETENTION_DATE_INVALID');
  const year = Number(new Intl.DateTimeFormat('en', { timeZone: 'Europe/Berlin', year: 'numeric' }).format(archivedAt));
  if (year < 1900 || year > 9988) throw new Error('ARCHIVE_RETENTION_DATE_INVALID');
  return new Date(Date.UTC(year + 11, 0, 1)).toISOString();
}

const instantSchema = z.union([
  z.string().max(64).datetime({ offset: true }).refine(value => !/\.\d{4}/.test(value) && Number.isFinite(Date.parse(value))),
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
    const instant = new Date(value + 'T00:00:00.000Z');
    return Number.isFinite(instant.getTime()) && instant.toISOString().slice(0, 10) === value;
  }),
]);
const assessmentSchema = z.object({
  archivedAt: instantSchema, retentionUntil: instantSchema, evaluatedAt: instantSchema,
  legalHold: z.boolean(), hasLegalHoldReason: z.boolean(),
  artifactStatus: z.enum(['VERIFIED', 'INVALID', 'NOT_CHECKED']),
}).strict();
export type RetentionBlockReason = 'INVALID_RETENTION_METADATA' | 'RETENTION_BELOW_POLICY'
  | 'ARCHIVE_DATE_IN_FUTURE' | 'LEGAL_HOLD' | 'LEGAL_HOLD_STATE_INVALID'
  | 'RETENTION_NOT_EXPIRED' | 'ARTIFACT_NOT_VERIFIED' | 'ARTIFACT_INVALID';
interface DecisionBase {
  policy: typeof ARCHIVE_RETENTION_POLICY;
  purgeAuthorized: false;
}
export type ArchiveRetentionDecision = DecisionBase & (
  | { status: 'RETENTION_SATISFIED'; reasons: []; effectiveRetentionUntil: string }
  | { status: 'BLOCKED'; reasons: [RetentionBlockReason, ...RetentionBlockReason[]]; effectiveRetentionUntil?: string }
);
const invalid = (): ArchiveRetentionDecision => ({ policy: ARCHIVE_RETENTION_POLICY, status: 'BLOCKED',
  reasons: ['INVALID_RETENTION_METADATA'], purgeAuthorized: false });

/** Pure snapshot-state decision. It cannot prove later holds, reference closure,
 * authorization, a write freeze or a resumable purge journal. */
export function assessArchiveRetention(input: unknown): ArchiveRetentionDecision {
  const parsed = assessmentSchema.safeParse(input);
  if (!parsed.success) return invalid();
  const value = parsed.data;
  const archivedAt = new Date(value.archivedAt);
  const storedUntil = new Date(value.retentionUntil).getTime();
  const evaluatedAt = new Date(value.evaluatedAt).getTime();
  let minimumUntil: number;
  try { minimumUntil = Date.parse(archiveRetentionUntil(archivedAt)); }
  catch { return invalid(); }
  const effectiveUntil = Math.max(minimumUntil, storedUntil);
  const reasons: RetentionBlockReason[] = [];
  if (storedUntil < minimumUntil) reasons.push('RETENTION_BELOW_POLICY');
  if (archivedAt.getTime() > evaluatedAt) reasons.push('ARCHIVE_DATE_IN_FUTURE');
  if (value.legalHold) reasons.push('LEGAL_HOLD');
  else if (value.hasLegalHoldReason) reasons.push('LEGAL_HOLD_STATE_INVALID');
  if (evaluatedAt < effectiveUntil) reasons.push('RETENTION_NOT_EXPIRED');
  if (value.artifactStatus === 'NOT_CHECKED') reasons.push('ARTIFACT_NOT_VERIFIED');
  if (value.artifactStatus === 'INVALID') reasons.push('ARTIFACT_INVALID');
  const effectiveRetentionUntil = new Date(effectiveUntil).toISOString();
  const [first, ...rest] = reasons;
  if (first !== undefined) return { policy: ARCHIVE_RETENTION_POLICY, status: 'BLOCKED',
    reasons: [first, ...rest], effectiveRetentionUntil, purgeAuthorized: false };
  return { policy: ARCHIVE_RETENTION_POLICY, status: 'RETENTION_SATISFIED', reasons: [],
    effectiveRetentionUntil, purgeAuthorized: false };
}
